import {
  DatabaseEnum,
  DatabaseRow,
} from "@Shape-Digital/kudzu-data/lib/types/common";
import { SupabaseClient } from "@supabase/supabase-js";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import { formatClientTimezoneToUTC_ISO } from "../../../common/dateHelpers";
import { DateRange } from "../../Unknown/DateRangePicker";

type AppointmentPackageCenterOffering = Pick<
  DatabaseRow<"center_offerings">,
  "name"
>;
export type AppointmentPackageOffering = Pick<
  DatabaseRow<"appointment_offerings">,
  | "status"
  | "total_cost_amount"
  | "has_unexpected_findings"
  | "is_receipt_confirmed"
> & {
  center_offering: AppointmentPackageCenterOffering;
};

type CenterAddOn = Pick<DatabaseRow<"center_add_ons">, "name">;
type AppointmentAddOn = Pick<
  DatabaseRow<"appointment_add_ons">,
  "total_cost_amount"
> & {
  center_add_on: CenterAddOn;
};

type CenterPackage = Pick<DatabaseRow<"center_packages">, "name">;
type AppointmentPackage = Pick<
  DatabaseRow<"appointment_packages">,
  "total_cost_amount"
> & {
  center_package: CenterPackage;
  appointment_offerings: AppointmentPackageOffering[];
  appointment_add_ons: AppointmentAddOn[];
};

type CenterOffering = Pick<DatabaseRow<"center_offerings">, "name">;
export type AppointmentOffering = Pick<
  DatabaseRow<"appointment_offerings">,
  | "side"
  | "status"
  | "total_cost_amount"
  | "has_unexpected_findings"
  | "is_receipt_confirmed"
  | "modality_name"
> & {
  center_offering: CenterOffering;
  appointment_add_ons: AppointmentAddOn[];
};

type CenterConfig = Pick<
  DatabaseRow<"center_configs">,
  "default_currency_code"
>;

type AppointmentCenter = Pick<
  DatabaseRow<"centers">,
  "name" | "region" | "timezone" | "country" | "state" | "postal_code"
> & {
  center_configs: CenterConfig[];
};

type PatientAddress = Pick<
  DatabaseRow<"patient_addresses">,
  "address" | "type"
>;

type Patient = Pick<
  DatabaseRow<"patients">,
  | "mrn"
  | "state"
  | "country"
  | "postal_code"
  | "city"
  | "race"
  | "ethnicity"
  | "sex"
> & {
  patient_addresses: PatientAddress[];
};

type AppointmentPatientDetails = Pick<
  DatabaseRow<"appointment_patient_details">,
  "first_name" | "last_name" | "date_birth" | "mobile_phone_number" | "email"
> & {
  patient: Patient;
};

type AppointmentCustomerDetails = Pick<
  DatabaseRow<"appointment_customer_details">,
  "first_name" | "last_name" | "type" | "customer_id"
>;

type AppointmentReport = Pick<
  DatabaseRow<"appointment_reports">,
  "id" | "status"
>;

type PaymentProvider = Pick<DatabaseRow<"payment_providers">, "type">;

export type AppointmentPayment = DatabaseRow<"appointment_payments"> & {
  center_payment_provider: {
    payment_provider: PaymentProvider;
  };
  center_payment_method: {
    payment_method: DatabaseRow<"payment_methods">;
  };
};

type AppointmentCoupon = Pick<DatabaseRow<"appointment_coupons">, "id"> & {
  coupon: Pick<DatabaseRow<"coupons">, "id" | "code">;
};

type AppointmentTimeSlot = Pick<DatabaseRow<"center_time_slots">, "started_at">;

export type DashboardAppointment = Pick<
  DatabaseRow<"appointments">,
  | "id"
  | "status"
  | "created_at"
  | "total_cost_amount"
  | "discount_amount"
  | "has_priority"
  | "is_stat"
  | "is_contacting_patient_required"
  | "has_previous_appointments"
  | "is_additional_views"
> & {
  appointment_patient_details: AppointmentPatientDetails[];
  appointment_customer_details: AppointmentCustomerDetails[];
  time_slot: AppointmentTimeSlot;
  center: AppointmentCenter;
  appointment_offerings: AppointmentOffering[];
  appointment_packages: AppointmentPackage[];
  appointment_add_ons: AppointmentAddOn[];
  appointment_payments: AppointmentPayment[];
  appointment_reports: AppointmentReport[];
  appointment_coupons: AppointmentCoupon[];
};

type SortField =
  | "status"
  | "date"
  | "time"
  | "location"
  | "locationState"
  | "locationZip";

type SortItem = {
  field: SortField;
  ascending: boolean;
};

const mapSortFieldToRpc = (field: SortField): string => {
  switch (field) {
    case "date":
    case "time":
      return "started_at";
    case "status":
      return "status";
    case "location":
      return "center_name";
    case "locationState":
      return "center_state";
    case "locationZip":
      return "center_zip";
    default:
      return "started_at";
  }
};

type RpcCursor = {
  started_at: string;
  id: string;
};

type RpcParams = {
  p_center_ids: string[];
  p_statuses: string[];
  p_from_date: string;
  p_to_date: string;
  p_sort_field: string;
  p_sort_ascending: boolean;
  p_sort_field_2: string | null;
  p_sort_ascending_2: boolean;
  p_sort_field_3: string | null;
  p_sort_ascending_3: boolean;
  p_page: number;
  p_page_size: number;
  p_center_name_filter: string | null;
  p_center_state_filter: string | null;
  p_center_zip_filter: string | null;
  p_cursor_started_at: string | null;
  p_cursor_id: string | null;
};

const buildRpcParams = (params: {
  centerIds: string[];
  statuses: DatabaseEnum["appointment_status"][];
  fromDate: string;
  toDate: string;
  sort?: SortItem[];
  filters?: GridFilterModel;
  timezone?: string | null;
  page: number;
  pageSize: number;
  cursor?: RpcCursor;
}): RpcParams => {
  const {
    centerIds,
    statuses,
    fromDate,
    toDate,
    sort,
    filters,
    timezone,
    page,
    pageSize,
    cursor,
  } = params;

  let effectiveFromDate = fromDate;
  let effectiveToDate = toDate;
  let effectiveStatuses = [...statuses] as string[];
  let centerNameFilter: string | null = null;
  let centerStateFilter: string | null = null;
  let centerZipFilter: string | null = null;

  if (filters?.items) {
    filters.items.forEach((item) => {
      const { field, operator, value } = item;
      if (value === undefined || value === null || value === "") return;

      if (field === "status") {
        if (operator === "is") {
          effectiveStatuses = [value as string];
        } else if (operator === "not") {
          effectiveStatuses = effectiveStatuses.filter((s) => s !== value);
        } else if (operator === "isAnyOf") {
          effectiveStatuses = effectiveStatuses.filter((s) =>
            (value as string[]).includes(s),
          );
        }
      } else if (field === "date" || field === "time") {
        const isDateField = field === "date";
        const dateValue =
          value instanceof Date ? value : new Date(value as string);

        if (operator === "is" && isDateField) {
          effectiveFromDate = formatClientTimezoneToUTC_ISO(
            startOfDay(dateValue),
            timezone,
          );
          effectiveToDate = formatClientTimezoneToUTC_ISO(
            endOfDay(dateValue),
            timezone,
          );
        } else if (operator === "after") {
          const afterDate = isDateField
            ? formatClientTimezoneToUTC_ISO(endOfDay(dateValue), timezone)
            : formatClientTimezoneToUTC_ISO(dateValue, timezone);
          // narrow from-date: after means strictly after, so use max
          if (afterDate > effectiveFromDate) {
            effectiveFromDate = afterDate;
          }
        } else if (operator === "onOrAfter") {
          const onOrAfterDate = isDateField
            ? formatClientTimezoneToUTC_ISO(startOfDay(dateValue), timezone)
            : formatClientTimezoneToUTC_ISO(dateValue, timezone);
          if (onOrAfterDate > effectiveFromDate) {
            effectiveFromDate = onOrAfterDate;
          }
        } else if (operator === "before") {
          const beforeDate = isDateField
            ? formatClientTimezoneToUTC_ISO(startOfDay(dateValue), timezone)
            : formatClientTimezoneToUTC_ISO(dateValue, timezone);
          if (beforeDate < effectiveToDate) {
            effectiveToDate = beforeDate;
          }
        } else if (operator === "onOrBefore") {
          const onOrBeforeDate = isDateField
            ? formatClientTimezoneToUTC_ISO(endOfDay(dateValue), timezone)
            : formatClientTimezoneToUTC_ISO(dateValue, timezone);
          if (onOrBeforeDate < effectiveToDate) {
            effectiveToDate = onOrBeforeDate;
          }
        }
      } else if (field === "location") {
        if (operator === "contains") {
          centerNameFilter = `%${value}%`;
        } else if (operator === "equals") {
          centerNameFilter = value as string;
        } else if (operator === "startsWith") {
          centerNameFilter = `${value}%`;
        } else if (operator === "endsWith") {
          centerNameFilter = `%${value}`;
        }
        // isAnyOf not supported via ILIKE — ignored (known limitation)
      } else if (field === "locationState") {
        if (operator === "contains") {
          centerStateFilter = `%${value}%`;
        } else if (operator === "equals") {
          centerStateFilter = value as string;
        } else if (operator === "startsWith") {
          centerStateFilter = `${value}%`;
        } else if (operator === "endsWith") {
          centerStateFilter = `%${value}`;
        }
      } else if (field === "locationZip") {
        if (operator === "contains") {
          centerZipFilter = `%${value}%`;
        } else if (operator === "equals") {
          centerZipFilter = value as string;
        } else if (operator === "startsWith") {
          centerZipFilter = `${value}%`;
        } else if (operator === "endsWith") {
          centerZipFilter = `%${value}`;
        }
      }
    });
  }

  const [s1, s2, s3] = sort ?? [];

  return {
    p_center_ids: centerIds,
    p_statuses: effectiveStatuses,
    p_from_date: effectiveFromDate,
    p_to_date: effectiveToDate,
    p_sort_field: s1 ? mapSortFieldToRpc(s1.field) : "started_at",
    p_sort_ascending: s1 ? s1.ascending : true,
    p_sort_field_2: s2 ? mapSortFieldToRpc(s2.field) : null,
    p_sort_ascending_2: s2 ? s2.ascending : true,
    p_sort_field_3: s3 ? mapSortFieldToRpc(s3.field) : null,
    p_sort_ascending_3: s3 ? s3.ascending : true,
    p_page: page,
    p_page_size: pageSize,
    p_center_name_filter: centerNameFilter,
    p_center_state_filter: centerStateFilter,
    p_center_zip_filter: centerZipFilter,
    p_cursor_started_at: cursor?.started_at ?? null,
    p_cursor_id: cursor?.id ?? null,
  };
};

const getDashboardAppointments = async (params: {
  supabase: SupabaseClient;
  centerIds: string[];
  date: Date;
  statuses: DatabaseEnum["appointment_status"][];
  dateRange?: DateRange<Date>;
  timezone?: string | null;
  pagination: { page: number; pageSize: number };
  sort?: SortItem[];
  filters?: GridFilterModel;
  cursor?: RpcCursor;
}) => {
  const {
    supabase,
    centerIds,
    date,
    statuses,
    dateRange,
    timezone,
    pagination,
    sort,
    filters,
    cursor,
  } = params;

  // Compute base date window (date picker or today)
  let fromDate: string;
  let toDate: string;

  if (dateRange?.[0] || dateRange?.[1]) {
    fromDate = dateRange[0]
      ? formatClientTimezoneToUTC_ISO(startOfDay(dateRange[0]), timezone)
      : formatClientTimezoneToUTC_ISO(startOfDay(date), timezone);
    toDate = dateRange[1]
      ? formatClientTimezoneToUTC_ISO(endOfDay(dateRange[1]), timezone)
      : formatClientTimezoneToUTC_ISO(endOfDay(date), timezone);
  } else {
    fromDate = formatClientTimezoneToUTC_ISO(startOfDay(date), timezone);
    toDate = formatClientTimezoneToUTC_ISO(endOfDay(date), timezone);
  }

  const rpcParams = buildRpcParams({
    centerIds,
    statuses,
    fromDate,
    toDate,
    sort,
    filters,
    timezone,
    page: pagination.page,
    pageSize: pagination.pageSize,
    cursor,
  });

  // Step 1: RPC to get sorted + paginated IDs and total count
  const { data: rpcData, error: rpcError } = (await supabase.rpc(
    "get_dashboard_appointment_ids",
    rpcParams,
  )) as {
    data: { id: string; started_at: string; total_count: number }[] | null;
    error: unknown;
  };

  if (rpcError) {
    throw new Error((rpcError as { message?: string }).message ?? "RPC error");
  }

  if (!rpcData || rpcData.length === 0) {
    return { data: [], count: 0, nextCursor: null };
  }

  const ids = rpcData.map((row) => row.id);
  const totalCount = rpcData[0].total_count;

  // Cursor for the next page: last row's (started_at, id), only when page is full
  const lastRpcRow = rpcData[rpcData.length - 1];
  const nextCursor: RpcCursor | null =
    rpcData.length === pagination.pageSize
      ? { started_at: lastRpcRow.started_at, id: lastRpcRow.id }
      : null;

  // Step 2: Fetch full appointment details for those IDs via RPC.
  // Using RPC instead of a PostgREST select to avoid PostgreSQL's generic plan
  // adoption after ~5 executions (which degrades performance from <1s to >5s).
  const { data, error } = await supabase.rpc(
    "get_dashboard_appointment_details",
    { p_ids: ids },
  );

  if (error) {
    throw new Error((error as { message?: string }).message ?? "RPC error");
  }

  // Re-order fetched rows to match the RPC's sorted order
  const byId = new Map(
    ((data ?? []) as DashboardAppointment[]).map((appt) => [appt.id, appt]),
  );
  const orderedData = ids
    .map((id) => byId.get(id))
    .filter((appt): appt is DashboardAppointment => appt !== undefined);

  return { data: orderedData, count: Number(totalCount), nextCursor };
};

export default getDashboardAppointments;

export const getAllDashboardAppointments = async (
  params: Omit<
    Parameters<typeof getDashboardAppointments>[0],
    "pagination" | "cursor"
  >,
): Promise<DashboardAppointment[]> => {
  const all: DashboardAppointment[] = [];
  let cursor: RpcCursor | undefined;
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    // Sequential pagination: each page depends on the previous cursor.
    // eslint-disable-next-line no-await-in-loop
    const result = await getDashboardAppointments({
      ...params,
      pagination: { page, pageSize: 100 },
      cursor,
    });
    all.push(...result.data);
    if (result.nextCursor) {
      cursor = result.nextCursor;
      page += 1;
    } else {
      hasMore = false;
    }
  }

  return all;
};
